import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_NUMBER_OF_MONTHS, DEFAULT_SELECTED_RANGE } from './constants';
import { DatePickerManagerOutput, DatePickerManagerProps, DateRange } from './DatePicker.types';
import { getInitialMonths } from './helpers';

const useDatePickerManager = ({
  numberOfMonthsToShow = DEFAULT_NUMBER_OF_MONTHS,
  defaultDateRange = DEFAULT_SELECTED_RANGE,
}: DatePickerManagerProps): DatePickerManagerOutput => {
  const [selectedRange, setSelectedRange] = useState<DateRange>(defaultDateRange);
  const today = useMemo(() => moment().startOf('month').toDate(), []);
  const visibleMonths = useMemo(() => getInitialMonths(today, numberOfMonthsToShow), [today, numberOfMonthsToShow]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      const normalizedDate = moment(date).startOf('day').toDate();

      if (!selectedRange.start || selectedRange.end) {
        setSelectedRange({ start: normalizedDate, end: null });
      } else {
        const momentNormalizedDate = moment(normalizedDate);
        const momentSelectedStart = moment(selectedRange.start);

        if (momentNormalizedDate.isSame(momentSelectedStart, 'day')) {
          setSelectedRange({ start: normalizedDate, end: normalizedDate });
        } else if (momentNormalizedDate.isBefore(momentSelectedStart, 'day')) {
          setSelectedRange({ start: normalizedDate, end: selectedRange.start });
        } else {
          setSelectedRange({ start: selectedRange.start, end: normalizedDate });
        }
      }
    },
    [selectedRange],
  );

  const resetSelection = () => {
    setSelectedRange(DEFAULT_SELECTED_RANGE);
  };

  return {
    selectedRange,
    visibleMonths,
    onDateSelect: handleDateSelect,
    resetSelection,
  };
};

export default useDatePickerManager;
